import { OutputBlockData } from "@editorjs/editorjs";
import React from "react";
import parse, { domToReact, Element } from "html-react-parser";
import sanitizeHtml from "sanitize-html";
import { getCurrentImage } from "../../utils/GeneralUtils";
import styles from "./Content.module.scss";

type Props = {
  content: Array<OutputBlockData>;
};
const Content: React.FC<Props> = ({ content }) => {
  const replaceFunc = (text: string) => {
    const replaceText = sanitizeHtml(
      text
        .replace(/&nbsp;/gi, " ")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/<br>/gi, `\n`),
      { allowedTags: ["a", "em", "code", "i"] }
    );

    if (!replaceText) return;

    const replace = (node: unknown) => {
      if (node instanceof Element && node.name === "a") {
        return (
          <a {...node.attribs} rel="noreferrer" target="_blank">
            {domToReact(node.children)}
          </a>
        );
      }
    };

    const c = parse(replaceText, { replace });

    return c;
  };

  return (
    <div className={styles["content"]}>
      {content.map((parts, i) => {
        switch (parts.type) {
          case "paragraph":
            return <p key={i}>{replaceFunc(parts.data.text)}</p>;
          case "header":
            if (parts.data.level === 2) {
              return (
                <h2 className={styles["header2"]} key={i}>
                  {replaceFunc(parts.data.text)}
                </h2>
              );
            } else if (parts.data.level === 3) {
              return (
                <h3 className={styles["header3"]} key={i}>
                  {replaceFunc(parts.data.text)}
                </h3>
              );
            } else {
              return null;
            }
          case "table":
            return (
              <table
                className={
                  parts.data.withHeadings
                    ? `${styles["table"]} ${styles["table-heading"]}`
                    : styles["table"]
                }
                key={i}
              >
                <tbody>
                  {parts.data.content?.map((tr: Array<string>, i: number) => (
                    <tr key={i}>
                      {tr.map((td: string, i: number) => (
                        <td key={i}>{replaceFunc(td)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          case "list":
            if (parts.data.style === "unordered") {
              return (
                <ul className={styles["unordered-list"]} key={i}>
                  {parts.data.items?.map((li: string, i: number) => (
                    <li key={i}>{replaceFunc(li)}</li>
                  ))}
                </ul>
              );
            } else {
              return (
                <ol className={styles["ordered-list"]} key={i}>
                  {parts.data.items?.map((li: string, i: number) => (
                    <li key={i}>{replaceFunc(li)}</li>
                  ))}
                </ol>
              );
            }
          case "image":
            return (
              <React.Fragment key={i}>
                {parts.data ? (
                  <div className={styles["imgBox"]}>
                    <img
                      className={styles["imgBox__img"]}
                      src={
                        parts.data instanceof File
                          ? getCurrentImage(parts.data)
                          : parts.data.large?.url
                      }
                    />
                  </div>
                ) : null}
              </React.Fragment>
            );
          case "breakLine":
            return <br key={i}></br>;
          default:
            return null;
        }
      })}
    </div>
  );
};

export default Content;
